import axios from 'axios';
import { triggerFeatureGate } from '../services/Contexts/FeatureGateContext';
import { getCookie, clearAllClientAuth } from '../utils/subdomainUtils';

const API = axios.create({
	baseURL: `${process.env.REACT_APP_API_URL}/api`, // Ensure this matches your backend API URL
	withCredentials: true,
	headers: {
		// Don't set Content-Type globally - let it be set per-request based on data type
		// This allows FormData to work properly with multipart/form-data boundary
		'ngrok-skip-browser-warning': 'true',
	},
});

// Attach token and portal host before every request
API.interceptors.request.use((config) => {
	// Prefer httpOnly cookie mirror, then localStorage
	let token = getCookie('authToken') || localStorage.getItem('token');

	if (token && token.trim() !== '') {
		config.headers.Authorization = `Bearer ${token}`;
	}

	if (typeof window !== 'undefined' && window.location?.host) {
		const isAuthRoute =
			config.url?.includes('/auth/login') ||
			config.url?.includes('/auth/register') ||
			config.url?.includes('/auth/logout');
		if (isAuthRoute) {
			config.headers['x-portal-host'] = window.location.host;
			const appMode = process.env.REACT_APP_APP_MODE;
			if (appMode === 'system' || appMode === 'tenant') {
				config.headers['x-app-mode'] = appMode;
			}
		}
	}

	// Check if this is a FormData request
	const isFormData = config.data instanceof FormData;

	// If NOT FormData, set Content-Type to JSON (for regular API calls)
	if (!isFormData) {
		config.headers['Content-Type'] = 'application/json';
	}
	// If it IS FormData, don't set Content-Type - let axios/browser handle it with proper boundary

	// Option 2: Extract from AuthenticatedUserInfoContext
	const userInfoString = localStorage.getItem('authenticatedUserInfo');

	if (userInfoString) {
		try {
			const userInfo = JSON.parse(userInfoString);
			if (userInfo) {
				config.params = {
					...config.params,
					companyId: userInfo.companyId,
					userId: userInfo.userId,
				};
			}
		} catch (error) {
			console.error('Axios interceptor - Error parsing userInfo:', error);
		}
	} else {
	}

	// final config prepared
	return config;
});

// Add response interceptor to handle errors
API.interceptors.response.use(
	(response) => {
		return response;
	},
	(error) => {
		console.error(
			'Axios response error:',
			error.response?.status,
			error.response?.data,
			error.config?.url,
		);

		// Handle feature gate errors
		if (
			error.response?.status === 403 &&
			(error.response?.data?.code === 'FEATURE_NOT_AVAILABLE' ||
				error.response?.data?.code === 'FEATURE_LIMIT_REACHED')
		) {
			triggerFeatureGate(error.response.data);
		}

		if (
			error.response?.status === 403 &&
			error.response?.data?.code === 'ACCOUNT_SUSPENDED'
		) {
			clearAllClientAuth();
		}

		if (error.response?.status === 401) {
			clearAllClientAuth();
		}

		return Promise.reject(error);
	},
);

export default API;
